import Image from 'next/image'
import parse, { attributesToProps } from 'html-react-parser'
import clsx from 'clsx'
import { m } from 'framer-motion'
import { useRouter } from 'next/router'
import { getTranslatedField, getExtension } from '@/libs/utils'
import type { ArtworkType } from '@/@types/microcms-type'
import type { MicroCMSImage } from 'microcms-js-sdk'

const ContentPicture = (image: MicroCMSImage, key: string) => {
    if (!image?.url) return null
    return (
        <div key={key} className="img-skelton">
            <Image
                src={image.url}
                width={image.width ?? 1}
                height={image.height ?? 1}
                alt="image"
                sizes="100vw"
                quality={100}
            />
        </div>
    )
}

const renderImages = (images: MicroCMSImage[]) => {
    return <>{images.map((image) => ContentPicture(image, image?.url))}</>
}

const getGridClasses = (row: { fieldId: string }): string => {
    const colCount =
        row.fieldId === 'two_images' || row.fieldId === 'three_images' ? 2 : 1
    const colClass = `grid-cols-${colCount} md:grid-cols-${
        row.fieldId === 'three_images' ? 3 : colCount
    }`
    const firstColClass =
        row.fieldId === 'three_images'
            ? 'first:[&>*]:col-span-2 md:first:[&>*]:col-span-1'
            : ''
    return clsx(
        '[&_*]:w-full [&>p]:text-v-sm [&>p]:w-default md:[&>p]:w-4/5 [&>p]:mx-auto',
        'grid gap-6 md:gap-12 lg:gap-20',
        colClass,
        firstColClass
    )
}

export const Gallery = ({ gallery }: Pick<ArtworkType, 'gallery'>) => {
    const locale = useRouter().locale

    return (
        <>
            {gallery.map((row, index) => {
                let content
                switch (row.fieldId) {
                    case 'single_image':
                        content = renderImages([row.image_1_1])
                        break
                    case 'two_images':
                        content = renderImages([row.image_1_2, row.image_2_2])
                        break
                    case 'three_images':
                        content = renderImages([
                            row.image_1_3,
                            row.image_2_3,
                            row.image_3_3
                        ])
                        break
                    case 'video_iframe':
                        const options = {
                            replace: ({ attribs, name }: any) => {
                                if (!attribs || name !== 'iframe') return
                                const props = attributesToProps(attribs)
                                return <iframe {...props} loading="lazy" />
                            }
                        }
                        // need to check if the content is valid
                        if (!row.iframe_field) break
                        const lazyLoadedIframe = parse(
                            row.iframe_field,
                            options
                        )
                        content = (
                            <div className="relative w-full pb-[calc(315/560*100%)] [&>iframe]:absolute [&>iframe]:top-0 [&>iframe]:left-0 [&>iframe]:h-full [&>iframe]:w-full img-skelton">
                                {lazyLoadedIframe}
                            </div>
                        )
                        break
                    case 'file':
                        content = (
                            <div className="img-skelton">
                                <video controls={false} muted autoPlay>
                                    <source
                                        src={row.file?.url}
                                        type={`video/${getExtension(
                                            row.file?.url
                                        )}`}
                                    />
                                    <p>Your browser does not support video.</p>
                                </video>
                            </div>
                        )
                        break
                    case 'description':
                        content = parse(
                            getTranslatedField({ field: row, locale })
                        )
                        break
                    default:
                        content = null
                        break
                }
                return (
                    <m.div
                        key={row.fieldId + index}
                        initial={{ opacity: 0 }}
                        whileInView={{ opacity: 1 }}
                        transition={{ duration: 0.4 }}
                        viewport={{ once: true, margin: '-5%' }}
                        className={getGridClasses(row)}
                    >
                        {content}
                    </m.div>
                )
            })}
        </>
    )
}
